// @ts-nocheck
import { getUrl } from '@aws-amplify/storage';

interface CacheEntry {
  url: string;
  expiry: number;
  originalPath: string;
}

class ImageCache {
  private cache: Map<string, CacheEntry> = new Map();
  private readonly DEFAULT_EXPIRY_MS = 13 * 60 * 1000; // 13 minutes (shorter than AWS 15min to be safe)

  /**
   * Get a cached signed URL or fetch a new one
   */
  async getSignedUrl(imagePath: string): Promise<string | null> {
    if (!imagePath) return null;

    const now = Date.now();
    const cached = this.cache.get(imagePath);

    // Return cached URL if it exists and hasn't expired
    if (cached && cached.expiry > now) {
      // console.log(`ImageCache: Using cached URL for ${imagePath}`);
      return cached.url;
    }

    try {
      // console.log(`ImageCache: Fetching new signed URL for ${imagePath}`);
      
      const { url } = await getUrl({
        path: imagePath,
        options: {
          validateObjectExistence: false,
          expiresIn: 900, // 15 minutes
        },
      });

      const signedUrl = url.toString();
      
      // Cache the new URL
      this.cache.set(imagePath, {
        url: signedUrl,
        expiry: now + this.DEFAULT_EXPIRY_MS,
        originalPath: imagePath,
      });

      return signedUrl;

    } catch (error) {
      console.warn(`ImageCache: Failed to sign URL for ${imagePath}`, error);

      // If we have an expired cached URL, remove it
      if (cached) {
        this.cache.delete(imagePath);
      }
      
      return null;
    }
  }

  /**
   * Preload signed URLs for multiple images
   */
  async preloadImages(imagePaths: string[]): Promise<void> {
    const promises = imagePaths
      .filter(path => path && path.trim() !== '')
      .map(path => this.getSignedUrl(path));
    
    try {
      await Promise.allSettled(promises);
    } catch (error) {
      console.error('ImageCache: Error preloading images:', error);
    }
  }

  /**
   * Clear expired entries from cache
   */
  clearExpired(): void {
    const now = Date.now();
    let clearedCount = 0;

    for (const [key, entry] of this.cache.entries()) {
      if (entry.expiry <= now) {
        this.cache.delete(key);
        clearedCount++;
      }
    }

    if (clearedCount > 0) {
      // console.log(`ImageCache: Cleared ${clearedCount} expired entries`);
    }
  }

  /**
   * Clear all cached entries
   */
  clear(): void {
    const size = this.cache.size;
    this.cache.clear();
    // console.log(`ImageCache: Cleared all ${size} cached entries`);
  }

  /**
   * Get cache statistics
   */
  getStats(): { size: number; entries: Array<{ path: string; expiry: Date; timeLeft: string }> } {
    const now = Date.now();
    const entries = Array.from(this.cache.entries()).map(([path, entry]) => ({
      path,
      expiry: new Date(entry.expiry),
      timeLeft: entry.expiry > now ? `${Math.round((entry.expiry - now) / 1000 / 60)}min` : 'expired'
    }));

    return {
      size: this.cache.size,
      entries
    };
  }

  // Drops the cached URL and signs a fresh one, for an <img> whose URL expired or failed
  async refreshSignedUrl(imagePath: string): Promise<string | null> {
    this.invalidate(imagePath);
    return this.getSignedUrl(imagePath);
  }

  /**
   * Invalidate cache for specific image path
   */
  invalidate(imagePath: string): void {
    if (this.cache.has(imagePath)) {
      this.cache.delete(imagePath);
      // console.log(`ImageCache: Invalidated cache for ${imagePath}`);
    }
  }
}

// Create singleton instance
export const imageCache = new ImageCache();

// Clean up expired entries every 5 minutes
setInterval(() => {
  imageCache.clearExpired();
}, 5 * 60 * 1000);

// Optional: Clear cache when tab becomes hidden (user switches tabs)
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      // Don't clear immediately, just clean expired entries
      imageCache.clearExpired();
    }
  });
}

export default imageCache;
